import { useCallback, useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { zodResolver } from '@hookform/resolvers/zod';
import { Button, Space, Tabs } from 'antd';
import dayjs from 'dayjs';
import {
  getDaysArray,
  getDaysObject,
} from 'pos-client/components/locations/utils/daysOptionsHelpers.ts';
import { PosModal } from 'pos-client/components/shared/Display/Modal/PosModal.tsx';
import { useModalForm } from 'pos-client/hooks/useModalForm.tsx';
import { useNotifications } from 'pos-client/hooks/useNotifications.tsx';
import { RouteAvailabilityFormRefinedSchema } from 'pos-client/schemas/RouteRefinedSchema.ts';
import {
  createRouteInitialSteps,
  createRouteTabs,
} from 'pos-client/tabs/createRouteTabs.tsx';
import { trpc } from 'pos-client/utils/trpc.ts';
import {
  RechargePolicyEnum,
  type RouteModel,
} from 'pos-shared/models/resourceManagementSchema.ts';
import {
  type RouteAvailabilityFormModel,
  type RouteBasicDetailsModel,
  RouteBasicDetailsSchema,
  type RouteCapacityModel,
  RouteCapacitySchema,
  type RouteRechargeSettingsModel,
  RouteRechargeSettingsSchema,
} from 'pos-shared/models/routeSchema.ts';

import type { Step } from './Step.ts';

const rechargePolicyOptions = RechargePolicyEnum.enum;

export interface CustomModalProps {
  isVisible: boolean;
  isEditMode: boolean;
  serviceLocationId: string;
  data: RouteModel | undefined;
  closeModal: () => void;
  handleOk: () => void;
  handleCancel: () => void;
}

export const RoutesModal = ({
  isVisible,
  isEditMode,
  serviceLocationId,
  data,
  handleOk,
  handleCancel,
}: CustomModalProps) => {
  const { t } = useTranslation();

  const { showSuccess, showError } = useNotifications();

  const [currentTab, setCurrentTab] = useState('1');

  const [okText, setOkText] = useState<string>(
    t('common.buttons.saveContinue')
  );

  const trpcContext = trpc.useUtils();

  const initialFormValues = {
    basicDetailsForm: {
      name: data?.name,
      description: data?.description,
      sameDay: data?.sameDay,
    },
    availabilityForm: {
      ...(data?.startTimeWindow && {
        startTimeWindow: {
          startTime: dayjs(data.startTimeWindow.startTime, 'HH:mm:ss'),
          endTime: dayjs(data.startTimeWindow.endTime, 'HH:mm:ss'),
        },
      }),
      ...(data?.finishTimeWindow && {
        finishTimeWindow: {
          startTime: dayjs(data.finishTimeWindow.startTime, 'HH:mm:ss'),
          endTime: dayjs(data.finishTimeWindow.endTime, 'HH:mm:ss'),
        },
      }),
      ...(data?.serviceTimeWindow && {
        serviceTimeWindow: {
          startTime: dayjs(data.serviceTimeWindow.startTime, 'HH:mm:ss'),
          endTime: dayjs(data.serviceTimeWindow.endTime, 'HH:mm:ss'),
        },
      }),
      daysOfTheWeek: getDaysArray(data?.daysOfTheWeek),
    },
    rechargeSettingsForm: {
      rechargeSettings: {
        allowRecharges: !!data?.rechargeSettings?.allowRecharges,
        rechargePolicy:
          data?.rechargeSettings?.rechargePolicy ??
          rechargePolicyOptions.AnyDepot,
        rechargeToCapacity: !!data?.rechargeSettings?.rechargeToCapacity,
      },
      settings: {
        limits: {
          maxDistance: data?.settings?.limits?.maxDistance ?? 0,
        },
      },
    },
    capacityForm: {
      capabilities: data?.capabilities,
      capacitySettings: data?.capacitySettings,
    },
  };

  const basicDetailsForm = useForm<RouteBasicDetailsModel>({
    resolver: zodResolver(RouteBasicDetailsSchema),
    mode: 'all',
  });

  const availabilityForm = useForm<RouteAvailabilityFormModel>({
    resolver: zodResolver(RouteAvailabilityFormRefinedSchema),
    mode: 'all',
  });

  const rechargeSettingsForm = useForm<RouteRechargeSettingsModel>({
    resolver: zodResolver(RouteRechargeSettingsSchema),
    mode: 'all',
  });

  const capacityForm = useForm<RouteCapacityModel>({
    resolver: zodResolver(RouteCapacitySchema),
    mode: 'all',
  });

  const routeInitialSteps = createRouteInitialSteps({
    basicDetailsForm,
    availabilityForm,
    rechargeSettingsForm,
    capacityForm,
  });

  /** Moving to fix hook issue but really all of this needs refactoring */
  useEffect(() => {
    if (isVisible) {
      setCurrentTab('1');
      basicDetailsForm.reset(initialFormValues.basicDetailsForm);
      availabilityForm.reset(initialFormValues.availabilityForm);
      rechargeSettingsForm.reset(initialFormValues.rechargeSettingsForm);
      capacityForm.reset(initialFormValues.capacityForm);
    }
  }, [isVisible]);

  const modalState = useModalForm(routeInitialSteps, {
    basicDetailsForm,
    availabilityForm,
    rechargeSettingsForm,
    capacityForm,
  });

  useEffect(() => {
    if (modalState.currentTab.key === routeInitialSteps.length - 1) {
      setOkText(t('common.buttons.save'));
    } else {
      setOkText(t('common.buttons.saveContinue'));
    }
  }, [modalState.currentTab.key]);

  const { mutate: updateRoute, isLoading: isUpdatingRoute } =
    trpc.updateRoute.useMutation({
      onError: (error) => {
        if (error) {
          return handleError(error);
        }
        showError(
          t('common.notification.error.message', {
            action: 'update',
            entity: 'route',
          })
        );
      },
      onSuccess: async () => {
        showSuccess(
          t('common.notification.success.message', {
            action: 'updated',
            entity: 'route',
          })
        );
        await trpcContext.getResourcesByLocationId.invalidate();
        handleOk();
        modalState.resetAllForms();
      },
    });

  const handleError = (error: any) => {
    const problemDetails = error.data?.problemDetails;
    const description = Array.isArray(problemDetails?.message)
      ? problemDetails?.message.map((item: any) => item.message).join(', ')
      : Object.values(problemDetails?.message?.errors).join(', ');

    showError(description);
  };

  const { mutate: createRoute, isLoading: isCreatingRoute } =
    trpc.createRoute.useMutation({
      onError: (error) => {
        if (error) {
          return handleError(error);
        }
        showError(
          t('common.notification.error.message', {
            action: 'create',
            entity: 'route',
          })
        );
      },
      onSuccess: async () => {
        showSuccess(
          t('common.notification.success.message', {
            action: 'created',
            entity: 'route',
          })
        );
        await trpcContext.getResourcesByLocationId.invalidate();
        handleOk();
        modalState.resetAllForms();
      },
    });

  const onCancelModal = () => {
    handleCancel();
    modalState.resetAllForms();
  };

  // This really needs to be refactored to use handleSubmit
  const handleSaveRoute = useCallback(() => {
    const {
      startTimeWindow,
      finishTimeWindow,
      serviceTimeWindow,
      ...availability
    } = availabilityForm.getValues();
    const formattedAvailability = {
      daysOfTheWeek: availability?.daysOfTheWeek?.length
        ? getDaysObject(availability.daysOfTheWeek)
        : undefined,
      startTimeWindow: {
        startTime: dayjs(startTimeWindow.startTime).format('HH:mm:ss'),
        endTime: dayjs(startTimeWindow.endTime).format('HH:mm:ss'),
      },
      finishTimeWindow: {
        startTime: dayjs(finishTimeWindow.startTime).format('HH:mm:ss'),
        endTime: dayjs(finishTimeWindow.endTime).format('HH:mm:ss'),
      },
      serviceTimeWindow: {
        startTime: dayjs(serviceTimeWindow.startTime).format('HH:mm:ss'),
        endTime: dayjs(serviceTimeWindow.endTime).format('HH:mm:ss'),
      },
    };

    const formValues = {
      ...basicDetailsForm.getValues(),
      ...formattedAvailability,
      ...rechargeSettingsForm.getValues(),
      ...capacityForm.getValues(),
      serviceLocationId: serviceLocationId,
    };

    if (isEditMode) {
      const payload = {
        ...formValues,
        id: data?.id,
      };

      updateRoute(payload as RouteModel);
    } else {
      const payload = {
        ...formValues,
      };

      if (modalState.currentTab.key === routeInitialSteps.length - 1) {
        createRoute(payload as RouteModel);
      } else {
        setCurrentTab(String(Number(currentTab) + 1));
      }
    }
  }, [
    availabilityForm,
    basicDetailsForm,
    rechargeSettingsForm,
    capacityForm,
    serviceLocationId,
    isEditMode,
    data?.id,
    updateRoute,
    modalState.currentTab.key,
    routeInitialSteps.length,
    createRoute,
    currentTab,
  ]);

  return (
    <PosModal
      header={{
        title: isEditMode ? t('routes.editRoute') : t('routes.addRoute'),
      }}
      width={724}
      open={isVisible}
      confirmLoading={isCreatingRoute || isUpdatingRoute}
      onCancel={onCancelModal}
      footer={
        <Space>
          <Button onClick={onCancelModal}>{t('common.buttons.cancel')}</Button>
          <Button
            type="primary"
            onClick={
              modalState.currentTab.key !== modalState.allSteps.length - 1
                ? modalState.saveStep
                : handleSaveRoute
            }
            loading={isCreatingRoute || isUpdatingRoute}
            disabled={
              modalState.currentTab.key !== modalState.allSteps.length &&
              modalState.isValid
                ? false
                : true
            }
          >
            {okText}
          </Button>
        </Space>
      }
    >
      <Tabs
        activeKey={modalState.currentTab.key.toString()}
        onChange={modalState.handleTabChange}
        items={createRouteTabs({
          basicDetailsForm,
          availabilityForm,
          rechargeSettingsForm,
          capacityForm,
          route: data,
          allSteps: modalState.allSteps as unknown as Step<RouteModel>[],
        })}
      />
    </PosModal>
  );
};
